/**
 * Subdomain routing utilities.
 *
 * Domain model:
 *   app.tailseed.com      — platform admin, support, individual users
 *   {slug}.tailseed.com   — enterprise users (slug = enterprise slug)
 *
 * The root app subdomain is read from the VITE_APP_SUBDOMAIN env var
 * (defaults to "app"). ROOT_DOMAIN is from VITE_ROOT_DOMAIN (e.g. "tailseed.com").
 * In development (localhost) all routing stays on localhost — no subdomain hopping.
 */

const ROOT_DOMAIN = import.meta.env.VITE_ROOT_DOMAIN ?? "";
const APP_SUBDOMAIN = import.meta.env.VITE_APP_SUBDOMAIN ?? "app";
const IS_DEV = !ROOT_DOMAIN || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

/** Returns the configured root domain, e.g. "tsnet.dev". Falls back to hostname in dev. */
export function rootDomain(): string {
  if (ROOT_DOMAIN) return ROOT_DOMAIN;
  // In dev, use the current hostname (e.g. "localhost")
  const parts = window.location.hostname.split(".");
  if (parts.length > 2) return parts.slice(1).join(".");
  return window.location.hostname;
}

/** Display label for a slug's URL, e.g. "acme.tsnet.dev" */
export function slugDomain(slug: string): string {
  if (IS_DEV) return `${slug}.localhost`;
  return `${slug}.${ROOT_DOMAIN}`;
}

/** Display label for the app subdomain URL */
export function appDomain(): string {
  if (IS_DEV) return "localhost";
  return `${APP_SUBDOMAIN}.${ROOT_DOMAIN}`;
}


/** Current subdomain, or null on localhost. */
export function getCurrentSubdomain(): string | null {
  if (IS_DEV) return null;
  const parts = window.location.hostname.split(".");
  if (parts.length <= 2) return null;
  return parts[0]!.toLowerCase();
}

/** True if we're on app.domain (the platform/individual subdomain). */
export function isAppSubdomain(): boolean {
  const sub = getCurrentSubdomain();
  return sub === null || sub === APP_SUBDOMAIN;
}

/** True if we're on an enterprise subdomain. */
export function isEnterpriseSubdomain(): boolean {
  return !isAppSubdomain();
}

/** Build the full URL for the app subdomain. */
export function appSubdomainUrl(path = "/"): string {
  if (IS_DEV) return path;
  const { protocol, port } = window.location;
  const portStr = port ? `:${port}` : "";
  return `${protocol}//${APP_SUBDOMAIN}.${ROOT_DOMAIN}${portStr}${path}`;
}

/** Build the full URL for a given enterprise slug. */
export function enterpriseSubdomainUrl(slug: string, path = "/"): string {
  if (IS_DEV) return path;
  const { protocol, port } = window.location;
  const portStr = port ? `:${port}` : "";
  return `${protocol}//${slug}.${ROOT_DOMAIN}${portStr}${path}`;
}

/**
 * Redirect after login based on user role and enterprise memberships.
 *
 * Rules:
 *  1. Platform admin / support → stay on app subdomain → /dashboard
 *  2. Enterprise user(s) → redirect to default enterprise subdomain
 *     (passes ?token= so the enterprise subdomain can pick up the session)
 *  3. No enterprise, no platform role → stay on app subdomain → /dashboard
 */
export function resolvePostLoginUrl(opts: {
  platformRole: string | null;
  defaultEnterpriseSlug: string | null;
  accessToken: string;
}): string {
  const { platformRole, defaultEnterpriseSlug, accessToken } = opts;

  // Platform admin / support always go to the app subdomain — and to
  // pages that actually work there. /dashboard renders the ENTERPRISE
  // dashboard, whose query cannot resolve a tenant on app.<domain> and
  // is guaranteed to 400; platform admins have their own dashboard at
  // /platform. Support cannot pass AdminRoute (platform_admin only), so
  // /platform would bounce them — /profile is their working landing.
  if (platformRole === "platform_admin") {
    if (isAppSubdomain()) return "/platform";
    return appSubdomainUrl("/platform");
  }
  if (platformRole === "support") {
    if (isAppSubdomain()) return "/profile";
    return appSubdomainUrl("/profile");
  }

  // Enterprise users go to their default enterprise subdomain
  if (defaultEnterpriseSlug) {
    if (IS_DEV) return "/dashboard"; // dev: stay local
    const currentSub = getCurrentSubdomain();
    if (currentSub === defaultEnterpriseSlug) return "/dashboard"; // already there
    // Cross-subdomain handoff: pass token as one-time URL param
    return enterpriseSubdomainUrl(defaultEnterpriseSlug, `/auth/handoff?token=${encodeURIComponent(accessToken)}`);
  }

  // Individual user — stay on app subdomain
  if (isAppSubdomain()) return "/dashboard";
  return appSubdomainUrl("/dashboard");
}
